import {
    ChangeDetectorRef,
    Component,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ButtonComponent } from "../../../../button/button.component";
import { IconButtonComponent } from "../../../../icon-button/icon-button.component";
import { InputComponent } from "../../../../input/input.component";
import { CoordsViewModel, IMapComponent } from "../../../map.models";
import { IMapGeometryPoint } from "../../../map.settings";
import { MapEditService } from "../../../services/map-edit.service";
import { SpatialService } from "../../../services/spatial.service";
import { GeometryInformation } from "../edit-menu/models/edit-models";
import { mapMarkerSvgPath } from "./icon-constants";

@Component({
    selector: "rask-navigate",
    templateUrl: "./navigate.component.html",
    styleUrls: ["./navigate.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        IconButtonComponent,
        ButtonComponent,
        InputComponent,
        FormsModule
    ]
})
export class NavigateComponent implements OnInit, OnDestroy {
    map: IMapComponent;

    protected copying = false;
    protected isOpen: boolean;

    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly idDrawLayer = "navigator-layer";
    private readonly mapEditService = inject(MapEditService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly ngZone = inject(NgZone);
    private readonly spatialService = inject(SpatialService);
    private readonly tresholdScale = [
        20000,
        250000
    ];

    private _data: CoordsViewModel;
    private ctrlThrottler: any;

    get data(): CoordsViewModel {
        return this._data;
    }

    @Input() set data(value: CoordsViewModel) {
        this._data = value;
        this._data.open$.pipe(takeUntil(this.ngUnsubscribe$), filter(d => ObjectUtils.hasValue(d))).subscribe(isOpen => {
            this.isOpen = isOpen;
            this.toggleLayer();
        });
    }

    ngOnDestroy(): void {
        const settings = this.map?.getMapSettings();
        if (settings?.drawLayers.find(l => l.id === this.idDrawLayer)) {
            this.map?.removeDrawLayer(this.idDrawLayer);
        }

        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.createLayerIfMissing();
    }

    protected close(): void {
        this._data.toggleMenu();
    }

    protected coordsCanCopy(): boolean {
        return this.mapEditService.canCopyToWindowsClipboard();
    }

    protected coordsCanPaste(): boolean {
        return true;
    }

    protected coordsClickNavigate(): void {
        let scale = this.map.getScaleLevel();
        if (scale < this.tresholdScale[0] || scale > this.tresholdScale[1]) {
            scale = this.tresholdScale[0];
        }
        this.map.gotoCoordinate(this._data.navigateToX, this._data.navigateToY, scale);
    }

    protected copyCurrentCoordinates(): void {
        this.copying = true;
        const mapGeometry = this.spatialService.getCenterPointFromMapExtent(this.map.getExtent());
        this.mapEditService.copyToWindowsClipboard([new GeometryInformation(mapGeometry, null)]).then(_ => {
            this.copying = false;
        });
    }

    protected inputChanged($event): void {
        this.checkXYinInput($event);
        this.updateInputValuesAndMarker();
    }

    protected async pasteCoordinates() {
        const coordinate = await this.mapEditService.pasteFromWindowsClipboard();
        if (coordinate.valid) {
            this._data.setNavigateCoordinate(coordinate.x, coordinate.y);
            this.updateInputValuesAndMarker();
        }
    }

    protected updateInputValuesAndMarker() {
        clearTimeout(this.ctrlThrottler);
        this.ctrlThrottler = setTimeout(() => {
            this.ngZone.run(() => {
                this.setMarker(this._data);
                this.changeDetectorRef.detectChanges();
            });
        }, 20);
    }

    private checkXYinInput($event): void {
        if (ObjectUtils.hasValue($event?.type)) {
            return;
        }
        if ($event.indexOf(" ") > -1) {
            this.parseXandY($event);
        } else {
            this.parseSingle($event);
        }
    }

    private createLayerIfMissing(): void {
        if (this.map) {
            const settings = this.map?.getMapSettings();
            if (!settings?.drawLayers.find(l => l.id === this.idDrawLayer)) {
                this.map?.createDrawLayer(this.idDrawLayer);
            }
            if (!settings?.icons.find(i => i.id === "mapmarkerIcon")) {
                settings.icons.push({
                    id: "mapmarkerIcon",
                    color: "#e74c3c",
                    size: 18,
                    outlineWidth: 0,
                    arcgisStyle: "path",
                    yoffset: 8,
                    outlineColor: "#e74c3c",
                    iconSvgPath: mapMarkerSvgPath.replace(/\n/g, "")
                });
            }
        }
    }

    private parseSingle(input: string): void {
        const value = parseInt(input, 10);
        if (isNaN(value)) {
            return;
        }
        this._data.updateNavigateCoordinate();
    }

    private parseXandY(input: string): void {
        const values = input.split(" ").map(n => parseInt(n, 10));
        if (values?.length === 2 && !values.some(x => isNaN(x))) {
            this._data.setNavigateCoordinate(values[0], values[1]);
        }
    }

    private setMarker(data: CoordsViewModel): void {
        if (data.isValid && this.map?.isLayerVisible(this.idDrawLayer)) {
            this.map?.clearGeometries(this.idDrawLayer);
            this.map?.addGeometries(this.idDrawLayer, [
{
    easting: data.navigateToX,
    northing: data.navigateToY,
    color: "red",
    id: "navigatemarker",
    label: `${data.x} ${data.y}`,
    visible: true,
    iconid: "mapmarkerIcon"
} as IMapGeometryPoint
            ]);
        }
    }

    private toggleLayer() {
        if (this.isOpen === true) {
            this.createLayerIfMissing();
            this.map?.showDrawLayer(this.idDrawLayer);
            this.setMarker(this._data);
        } else {
            this.map?.clearGeometries(this.idDrawLayer);
            this.map?.hideDrawLayer(this.idDrawLayer);
        }
    }
}
